import { Text } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/dist/tokens';
import styled from '@emotion/styled';

import { ShopInfo_representative_coupon_v2, ShopUxUbl, useDayLeftTimer } from '@domains/storehome-common';

import couponCenter from './image/coupon_center.svg';
import couponLeft from './image/coupon_left.svg';
import couponRight from './image/coupon_right.svg';

interface CouponDownloadClickProps {
  link?: string;
  ubl?: ShopUxUbl | null;
}

interface CouponProps {
  coupon: ShopInfo_representative_coupon_v2;
  link: string;
  onClick?: (params: CouponDownloadClickProps) => void;
}

export const Coupon = ({ coupon, link, onClick }: CouponProps) => {
  const { formatted_time } = useDayLeftTimer({
    end_timestamp: coupon.date_issue_expire ?? coupon.date_issue_end,
    timer_threshold_days: coupon.day_limit ?? 7,
    enable_smooth_transition: false,
  });

  const showDescription = coupon.description?.text || formatted_time;

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (onClick) {
      e.preventDefault();
      onClick?.({ link, ubl: coupon.ubl });
    }
  };

  return (
    <CouponWrap href={link} target='_blank' onClick={handleClick}>
      <Icon>
        <IconCenter />
      </Icon>
      <Content>
        {coupon.main_title.html?.normal ? (
          <Text
            as='p'
            variant='Body_14_Multiline_Bold'
            color='staticWhite'
            dangerouslySetInnerHTML={{ __html: coupon.main_title.html.normal }}
          />
        ) : (
          <Text as='p' variant='Body_14_Multiline_Bold' color='staticWhite'>
            {coupon.main_title.text}
          </Text>
        )}
        {showDescription && (
          <Description>
            {coupon.description?.text && (
              <Text
                className='coupon-description'
                as='span'
                variant='Caption_10_Multiline_Regular'
                color='textQuaternary'
              >
                {coupon.description?.text}
              </Text>
            )}
            {formatted_time && (
              <Text as='span' variant='Caption_10_Multiline_Regular' color='textQuaternary'>
                {formatted_time}
              </Text>
            )}
          </Description>
        )}
      </Content>
    </CouponWrap>
  );
};

const CouponWrap = styled.a`
  display: flex;
  align-items: center;
  position: relative;
  background-color: ${vars.color.backgroundSurface};
`;

const Icon = styled.i`
  display: flex;
  position: relative;
  width: 100%;
  height: 68px;
  font-size: 0; /* 인라인 요소 간격 제거 */

  &::before {
    display: block;
    content: '';
    background-image: url(${couponLeft});
    background-repeat: no-repeat;
    background-size: cover;
    width: 16px;
    height: 68px;
    flex-shrink: 0;
    margin-right: -1px; /* 겹침으로 공백 제거 */
  }

  &::after {
    display: block;
    content: '';
    background-image: url(${couponRight});
    background-repeat: no-repeat;
    background-size: cover;
    width: 76px;
    height: 68px;
    flex-shrink: 0;
    margin-left: -1px; /* 겹침으로 공백 제거 */
  }
`;

const IconCenter = styled.i`
  flex: 1;
  background-image: url(${couponCenter});
  background-repeat: repeat-x;
  background-size: auto 68px;
  height: 68px;
  display: block;
`;

const Content = styled.div`
  position: absolute;
  overflow: hidden;
  width: 100%;
  height: 68px;
  padding: 0 76px 0 18px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 3px;
`;

const Description = styled.div`
  display: flex;
  align-items: center;
  .coupon-description {
    position: relative;
    padding-right: 10px;
    &::after {
      content: '';
      position: absolute;
      margin: 0 4px;
      width: 2px;
      height: 2px;
      border-radius: 100%;
      background-color: ${vars.color.iconQuaternary};
      top: 50%;
      transform: translateY(-50%);
    }
  }
`;
